/**
 * Centralized Branding Configuration
 *
 * This file contains all branding and company-related configuration.
 * Modify these values to customize the application for your company.
 *
 * USAGE:
 * 1. Update the values in `brandingConfig` below
 * 2. Replace `resources/company-logo.png` with your actual logo
 * 3. The changes will be applied throughout the application
 */

// Logo import - centralized for easy replacement
import companyLogo from '/resources/company-logo.png';

/**
 * Company details interface for PDF/print templates
 */
export interface CompanyDetails {
  name: string;
  shortName: string;
  address: string;
  addressLine2?: string;
  cityStatePin: string;
  phone: string;
  email: string;
  website: string;
  gstin?: string;
  pan?: string;
  state?: string;
  stateCode?: string;
  logoUrl?: string;
}

/**
 * Branding configuration interface
 */
export interface BrandingConfig {
  // Application identity
  appName: string;
  appShortName: string;
  appDescription: string;

  // Company details for documents
  company: CompanyDetails;

  // Visual branding
  logo: {
    primary: string;
    favicon: string;
    printLogo?: string;
  };

  // URLs and links
  urls: {
    website: string;
    homepage: string;
    support?: string;
  };

  // Document settings
  documents: {
    footerText: string;
    termsAndConditions: string[];
    thankYouMessage: string;
  };
}

/**
 * Default branding configuration
 *
 * ============================================
 * CUSTOMIZE THESE VALUES FOR YOUR COMPANY
 * ============================================
 */
export const brandingConfig: BrandingConfig = {
  // Application identity
  appName: 'Clara Builders',
  appShortName: 'CB',
  appDescription: 'Clara Builders - Construction Business Management Platform',

  // Company details for documents (invoices, receipts, etc.)
  company: {
    name: 'Clara Builders Pvt. Ltd.',
    shortName: 'Clara Builders',
    address: '123 Main Street',
    addressLine2: '',
    cityStatePin: 'City, State 123456',
    phone: '1234567890',
    email: 'info@clarabuilders.com',
    website: 'www.clarabuilders.com',
    gstin: 'XXAAACXXXXXX1ZX',
    pan: 'AAACXXXXXF',
    state: 'Your State',
    stateCode: 'XX',
  },

  // Visual branding
  logo: {
    primary: companyLogo,
    favicon: companyLogo,
    printLogo: companyLogo,
  },

  // URLs and links
  urls: {
    website: 'https://clarabuilders.smart-apps.in',
    homepage: 'https://clarabuilders.smart-apps.in',
    support: 'https://clarabuilders.smart-apps.in/support',
  },

  // Document settings
  documents: {
    footerText: 'Thanks for doing business with us!',
    termsAndConditions: [
      'Subject to local jurisdiction',
      'E. & O.E.',
    ],
    thankYouMessage: 'Thank You! Visit Again',
  },
};

// Export individual values for convenience
export const {
  appName,
  appShortName,
  appDescription,
  company,
  logo,
  urls,
  documents,
} = brandingConfig;

// Export logo URL directly for simple imports
export const companyLogoUrl = brandingConfig.logo.primary;

/**
 * Helper function to get company details for PDF templates
 * This maintains backward compatibility with existing code
 */
export function getCompanyDetailsForPrint(): CompanyDetails {
  return {
    ...brandingConfig.company,
    logoUrl: brandingConfig.logo.printLogo || brandingConfig.logo.primary,
  };
}

/**
 * Get formatted company address for display
 */
export function getFormattedAddress(): string {
  const { address, addressLine2, cityStatePin } = brandingConfig.company;
  const lines = [address];
  if (addressLine2) lines.push(addressLine2);
  lines.push(cityStatePin);
  return lines.join('\n');
}

// Default export for simple usage
export default brandingConfig;
